'use client';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import type { AttributesEntity, ValuesEntity } from '@eliadmin/core/src/api/products/types/product-types';
import type { Category } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import { createContextStore } from '@eliadmin/core/src/stores/context-store';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import {
  applyCategoryFilterChange,
  createDefaultCategoryFilter,
  normalizeCategoryPage,
  parseCategoryFilterState,
  writeCategoryFilterState,
  type CategoryFilterState,
  type CategoryFilterUpdate,
} from '@/lib/category-filter-state';

// Mediatip pilot based on core 3.0.84. Keep public names/action inputs compatible.
// The shared store has no atomic URL or metadata update seam yet.
export type SelectedValue = { attribute: AttributesEntity; value: ValuesEntity };

export enum AttributeType {
  RADIO = 'radio',
  COLOR = 'color',
  CHECKBOX = 'checkbox',
  SELECT = 'select',
  IMAGE = 'image',
  SIZE = 'size',
  RANGE = 'range',
  SLIDER_SELECT = 'slider/select',
  SLIDER_RADIO = 'slider/radio',
  MULTICOLOR = 'multicolor',
}

export type CategoryStore = {
  category: Category & { slug: string };
  categoryTree: CategoriesEntity[];
  filter: CategoryFilterState;
  clearTrigger: boolean;
  page: number;
  paramsReady: boolean;
};

type CategoryActions = {
  clearFilters: () => void;
  setFilter: (update: CategoryFilterUpdate) => void;
  setManufacturers: (manufacturers: number[]) => void;
  setCategory: (category: CategoryStore['category']) => void;
  setPage: (page: number) => void;
  restoreUrl: (params: URLSearchParams) => void;
};

const [CategoryProvider, useCategoryStore] = createContextStore<CategoryStore, CategoryActions>(
  {
    category: {} as CategoryStore['category'],
    categoryTree: [],
    filter: createDefaultCategoryFilter(),
    clearTrigger: false,
    page: 1,
    paramsReady: false,
  },
  (set, get) => {
    let synchronizedSearch: string | undefined;

    const writeUrl = (mode: 'push' | 'replace') => {
      if (typeof window === 'undefined') return;
      const next = writeCategoryFilterState(new URLSearchParams(window.location.search), get());
      synchronizedSearch = next.toString();
      const search = synchronizedSearch ? `?${synchronizedSearch}` : '';
      if (window.location.search === search) return;
      const url = `${window.location.pathname}${search}${window.location.hash}`;
      // Native history is integrated with Next's router; no RSC refetch is needed.
      if (mode === 'push') window.history.pushState(null, '', url);
      else window.history.replaceState(null, '', url);
    };

    const initializeIfNeeded = (state: CategoryStore) => {
      if (state.paramsReady || typeof window === 'undefined') return;
      Object.assign(state, parseCategoryFilterState(new URLSearchParams(window.location.search)));
      state.paramsReady = true;
    };

    return {
      clearFilters: () => {
        set(state => {
          state.filter = createDefaultCategoryFilter();
          state.page = 1;
          state.paramsReady = true;
          state.clearTrigger = !state.clearTrigger;
        });
        writeUrl('replace');
      },
      setFilter: update => {
        set(state => {
          initializeIfNeeded(state);
          state.filter = applyCategoryFilterChange(state.filter, update);
          state.page = 1;
        });
        writeUrl('replace');
      },
      setManufacturers: manufacturers => {
        set(state => {
          initializeIfNeeded(state);
          state.filter.manufacturers = [...manufacturers];
          state.page = 1;
        });
        writeUrl('replace');
      },
      setCategory: category => {
        set(state => {
          state.category = category;
        });
      },
      setPage: page => {
        set(state => {
          initializeIfNeeded(state);
          state.page = normalizeCategoryPage(page);
        });
        writeUrl('push');
      },
      restoreUrl: params => {
        const search = params.toString();
        if (get().paramsReady && synchronizedSearch === search) return;
        synchronizedSearch = search;
        const next = parseCategoryFilterState(params);
        set(state => {
          // Filters and page are restored together, including when going Back.
          state.filter = next.filter;
          state.page = next.page;
          state.paramsReady = true;
        });
      },
    };
  },
);

function CategoryParamsSync() {
  const restoreUrl = useCategoryStore(state => state.restoreUrl);
  const pathname = usePathname();
  const search = useSearchParams().toString();

  useEffect(() => {
    // Read the actual URL, not a stale router snapshot from before a filter click.
    const restore = () => restoreUrl(new URLSearchParams(window.location.search));
    restore();
    window.addEventListener('popstate', restore);
    return () => window.removeEventListener('popstate', restore);
  }, [pathname, search, restoreUrl]);

  return null;
}

export { CategoryParamsSync, CategoryProvider, useCategoryStore };
